import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  insuranceNoteRuleId: '',
  treatmentCode: '',
  displayName: '',
  treatmentText: '',
  allowPatientSpecificEdit: true,
  showTotalTreatmentTime: true,
  totalTreatmentTimeRequired: false,
  defaultTotalMinutes: '',
  copyForward: false,
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    insuranceNoteRuleId:
      template?.insuranceNoteRuleId === null ||
      template?.insuranceNoteRuleId === undefined
        ? ''
        : String(template.insuranceNoteRuleId),
    treatmentCode: template?.treatmentCode || '',
    displayName: template?.displayName || '',
    treatmentText: template?.treatmentText || '',
    allowPatientSpecificEdit: Boolean(template?.allowPatientSpecificEdit),
    showTotalTreatmentTime: Boolean(template?.showTotalTreatmentTime),
    totalTreatmentTimeRequired: Boolean(template?.totalTreatmentTimeRequired),
    defaultTotalMinutes:
      template?.defaultTotalMinutes === null ||
      template?.defaultTotalMinutes === undefined
        ? ''
        : String(template.defaultTotalMinutes),
    copyForward: Boolean(template?.copyForward),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateOptionalPositiveInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '';
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return message;
  }

  return '';
}

function validateOptionalNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '';
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateRequiredNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateForm(form) {
  const errors = {};
  const treatmentCode = String(form.treatmentCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const treatmentText = String(form.treatmentText || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  const bodyPartError = validateOptionalPositiveInteger(
    form.bodyPartTemplateId,
    'Body-part template ID must be a positive whole number.'
  );

  if (bodyPartError) {
    errors.bodyPartTemplateId = bodyPartError;
  }

  const insuranceRuleError = validateOptionalPositiveInteger(
    form.insuranceNoteRuleId,
    'Insurance-note rule ID must be a positive whole number.'
  );

  if (insuranceRuleError) {
    errors.insuranceNoteRuleId = insuranceRuleError;
  }

  if (!treatmentCode) {
    errors.treatmentCode = 'Treatment code is required.';
  } else if (treatmentCode.length > 150) {
    errors.treatmentCode = 'Treatment code cannot exceed 150 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(treatmentCode)) {
    errors.treatmentCode =
      'Treatment code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Treatment display name is required.';
  } else if (displayName.length > 180) {
    errors.displayName = 'Treatment display name cannot exceed 180 characters.';
  }

  if (!treatmentText) {
    errors.treatmentText = 'Treatment text is required.';
  }

  const defaultMinutesError = validateOptionalNonNegativeInteger(
    form.defaultTotalMinutes,
    'Default total treatment minutes must be a whole number of 0 or greater.'
  );

  if (defaultMinutesError) {
    errors.defaultTotalMinutes = defaultMinutesError;
  }

  const displayOrderError = validateRequiredNonNegativeInteger(
    form.displayOrder,
    'Display order must be a whole number of 0 or greater.'
  );

  if (displayOrderError) {
    errors.displayOrder = displayOrderError;
  }

  if (adminNotes.length > 1000) {
    errors.adminNotes = 'Admin notes cannot exceed 1000 characters.';
  }

  return errors;
}

function buildRequest(form) {
  const bodyPartText = String(form.bodyPartTemplateId || '').trim();
  const insuranceRuleText = String(form.insuranceNoteRuleId || '').trim();
  const defaultMinutesText = String(form.defaultTotalMinutes || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  return {
    bodyPartTemplateId: bodyPartText ? Number(bodyPartText) : null,
    insuranceNoteRuleId: insuranceRuleText ? Number(insuranceRuleText) : null,
    treatmentCode: String(form.treatmentCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    treatmentText: String(form.treatmentText || '').trim(),
    allowPatientSpecificEdit: Boolean(form.allowPatientSpecificEdit),
    showTotalTreatmentTime: Boolean(form.showTotalTreatmentTime),
    totalTreatmentTimeRequired: Boolean(form.totalTreatmentTimeRequired),
    defaultTotalMinutes: defaultMinutesText ? Number(defaultMinutesText) : null,
    copyForward: Boolean(form.copyForward),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: adminNotes || null,
  };
}

function resolveScope(template) {
  const hasBodyPart =
    template?.bodyPartTemplateId !== null &&
    template?.bodyPartTemplateId !== undefined;
  const hasInsurance =
    template?.insuranceNoteRuleId !== null &&
    template?.insuranceNoteRuleId !== undefined;

  if (hasBodyPart && hasInsurance) {
    return 'BODY_AND_INSURANCE';
  }

  if (hasBodyPart) {
    return 'BODY_PART';
  }

  if (hasInsurance) {
    return 'INSURANCE';
  }

  return 'GLOBAL';
}

function scopeLabel(scope) {
  switch (scope) {
    case 'BODY_AND_INSURANCE':
      return 'Body Part + Insurance';
    case 'BODY_PART':
      return 'Body Part';
    case 'INSURANCE':
      return 'Insurance Rule';
    default:
      return 'Global';
  }
}

function truncateText(value, maxLength = 180) {
  const text = String(value || '').trim();

  if (!text) {
    return 'No treatment wording';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}...`;
}

export default function TreatmentTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [scopeFilter, setScopeFilter] = useState('ALL');
  const [copyForwardFilter, setCopyForwardFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.treatmentTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        const scope = resolveScope(template);

        if (scopeFilter !== 'ALL' && scope !== scopeFilter) {
          return false;
        }

        if (copyForwardFilter === 'YES' && !template.copyForward) {
          return false;
        }

        if (copyForwardFilter === 'NO' && template.copyForward) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

        return [
          template.treatmentTemplateId,
          template.treatmentCode,
          template.displayName,
          template.treatmentText,
          template.bodyPartTemplateId,
          bodyPart?.displayName,
          bodyPart?.bodyPartCode,
          template.insuranceNoteRuleId,
          template.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [
    templates,
    searchTerm,
    statusFilter,
    scopeFilter,
    copyForwardFilter,
    bodyPartMap,
  ]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  const globalCount = useMemo(
    () => templates.filter((template) => resolveScope(template) === 'GLOBAL').length,
    [templates]
  );

  const copyForwardCount = useMemo(
    () => templates.filter((template) => template.copyForward).length,
    [templates]
  );

  useEffect(() => {
    loadConfiguration();
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const [templateData, bodyPartData] = await Promise.all([
        clinicalTemplateAdminService.getAllTreatmentTemplates(),
        clinicalTemplateAdminService.getAllBodyPartTemplates(),
      ]);

      setTemplates(Array.isArray(templateData) ? templateData : []);
      setBodyParts(Array.isArray(bodyPartData) ? bodyPartData : []);
    } catch (error) {
      setTemplates([]);
      setBodyParts([]);
      setLoadError(
        error?.message || 'Treatment template configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateTreatmentTemplate(
          editingTemplate.treatmentTemplateId,
          request
        );
        showSuccess('Treatment template updated.');
      } else {
        await clinicalTemplateAdminService.createTreatmentTemplate(request);
        showSuccess('Treatment template created.');
      }

      setEditingTemplate(null);
      setForm({ ...EMPTY_FORM });
      setFormErrors({});
      await loadConfiguration();
    } catch (error) {
      setActionError(error?.message || 'Treatment template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Historical clinical notes will keep their saved treatment snapshots.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.treatmentTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setTreatmentTemplateActive(
        template.treatmentTemplateId,
        nextActive
      );

      showSuccess(
        `Treatment template ${nextActive ? 'activated' : 'deactivated'}.`
      );

      await loadConfiguration();
    } catch (error) {
      setActionError(
        error?.message || 'Treatment template status could not be updated.'
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="clinical-template-section-layout">
      {success && (
        <div className="clinical-template-message success" role="status">
          {success}
        </div>
      )}

      {actionError && (
        <div className="clinical-template-message error" role="alert">
          {actionError}
        </div>
      )}

      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading">
          <span className="clinical-template-eyebrow">
            TREATMENT TEMPLATE CONFIGURATION
          </span>
          <h2>{isEditing ? 'Edit Treatment Template' : 'Create Treatment Template'}</h2>
          <p>
            Configure reusable treatment wording globally, by body part, by
            insurance-note rule, or by both. The stable treatment code cannot
            be changed after creation.
          </p>
        </div>

        <form onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body-Part Restriction
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  updateFormField('bodyPartTemplateId', event.target.value)
                }
                disabled={saving}
              >
                <option value="">No body-part restriction</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
            </label>

            <label>
              Insurance-Note Rule ID
              <input
                type="number"
                min="1"
                step="1"
                value={form.insuranceNoteRuleId}
                onChange={(event) =>
                  updateFormField('insuranceNoteRuleId', event.target.value)
                }
                disabled={saving}
                placeholder="Optional"
              />
              <small>
                Leave blank for no insurance restriction. This ID will map to
                the Insurance Note Rules configuration section.
              </small>
              {formErrors.insuranceNoteRuleId && (
                <span className="clinical-template-field-error">
                  {formErrors.insuranceNoteRuleId}
                </span>
              )}
            </label>

            <label>
              Treatment Code
              <input
                type="text"
                value={form.treatmentCode}
                onChange={(event) =>
                  updateFormField('treatmentCode', event.target.value)
                }
                maxLength={150}
                disabled={saving || isEditing}
                placeholder="FIDELIS_ANKLE"
              />
              <small>
                Stable internal identifier. It cannot be changed after
                creation.
              </small>
              {formErrors.treatmentCode && (
                <span className="clinical-template-field-error">
                  {formErrors.treatmentCode}
                </span>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={180}
                disabled={saving}
                placeholder="Fidelis Ankle Treatment"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Default Total Minutes
              <input
                type="number"
                min="0"
                step="1"
                value={form.defaultTotalMinutes}
                onChange={(event) =>
                  updateFormField('defaultTotalMinutes', event.target.value)
                }
                disabled={saving}
                placeholder="Optional"
              />
              {formErrors.defaultTotalMinutes && (
                <span className="clinical-template-field-error">
                  {formErrors.defaultTotalMinutes}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Treatment Wording
            <textarea
              rows={9}
              value={form.treatmentText}
              onChange={(event) =>
                updateFormField('treatmentText', event.target.value)
              }
              disabled={saving}
              placeholder="Enter reusable treatment wording..."
            />
            <small>
              This is the reusable clinical wording. The DTO does not impose an
              artificial text-length limit.
            </small>
            {formErrors.treatmentText && (
              <span className="clinical-template-field-error">
                {formErrors.treatmentText}
              </span>
            )}
          </label>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.allowPatientSpecificEdit}
                onChange={(event) =>
                  updateFormField('allowPatientSpecificEdit', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Allow Patient-Specific Editing</strong>
                <small>
                  Clinicians may change the treatment wording on an individual
                  patient note.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.showTotalTreatmentTime}
                onChange={(event) =>
                  updateFormField('showTotalTreatmentTime', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Show Total Treatment Time</strong>
                <small>
                  Display total treatment minutes when this template is used.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.totalTreatmentTimeRequired}
                onChange={(event) =>
                  updateFormField(
                    'totalTreatmentTimeRequired',
                    event.target.checked
                  )
                }
                disabled={saving}
              />
              <span>
                <strong>Require Total Treatment Time</strong>
                <small>
                  Require treatment minutes before the clinical note can be
                  finalized.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.copyForward}
                onChange={(event) =>
                  updateFormField('copyForward', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Copy Forward</strong>
                <small>
                  Permit this wording to be copied forward when the backend
                  payer and prior-note rules also allow it.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>
                  Active templates may be selected for new clinical notes.
                </small>
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              rows={4}
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={1000}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            <small>{String(form.adminNotes || '').length}/1000</small>
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Update Treatment Template'
                  : 'Create Treatment Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-list-panel">
        <div className="clinical-template-editor-heading-row">
          <div className="clinical-template-editor-heading">
            <span className="clinical-template-eyebrow">EXISTING TEMPLATES</span>
            <h2>Treatment Templates</h2>
            <p>
              {templates.length} total, {activeCount} active, {globalCount}{' '}
              global, {copyForwardCount} configured for copy-forward.
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadConfiguration}
            disabled={loading || saving}
          >
            Refresh
          </button>
        </div>

        <div className="clinical-template-list-toolbar">
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search treatment templates..."
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>

          <select
            value={scopeFilter}
            onChange={(event) => setScopeFilter(event.target.value)}
          >
            <option value="ALL">All scopes</option>
            <option value="GLOBAL">Global</option>
            <option value="BODY_PART">Body part</option>
            <option value="INSURANCE">Insurance rule</option>
            <option value="BODY_AND_INSURANCE">Body part + insurance</option>
          </select>

          <select
            value={copyForwardFilter}
            onChange={(event) => setCopyForwardFilter(event.target.value)}
          >
            <option value="ALL">All copy-forward settings</option>
            <option value="YES">Copy forward</option>
            <option value="NO">No copy forward</option>
          </select>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="clinical-template-empty-state">
            Loading treatment templates...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No treatment templates match the current filters.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Template</th>
                  <th>Scope</th>
                  <th>Treatment Time</th>
                  <th>Copy Forward</th>
                  <th>Status</th>
                  <th>Audit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => {
                  const bodyPart = bodyPartMap.get(
                    Number(template.bodyPartTemplateId)
                  );
                  const scope = resolveScope(template);
                  const busy =
                    activeActionId === template.treatmentTemplateId;

                  return (
                    <tr key={template.treatmentTemplateId}>
                      <td>{template.displayOrder}</td>
                      <td>
                        <strong>{template.displayName}</strong>
                        <div className="clinical-template-code">
                          {template.treatmentCode}
                        </div>
                        <small>{truncateText(template.treatmentText)}</small>
                      </td>
                      <td>
                        <strong>{scopeLabel(scope)}</strong>
                        {template.bodyPartTemplateId !== null &&
                          template.bodyPartTemplateId !== undefined && (
                            <div>
                              Body: {bodyPart?.displayName || 'Unknown'} (#
                              {template.bodyPartTemplateId})
                            </div>
                          )}
                        {template.insuranceNoteRuleId !== null &&
                          template.insuranceNoteRuleId !== undefined && (
                            <div>
                              Insurance Rule #{template.insuranceNoteRuleId}
                            </div>
                          )}
                      </td>
                      <td>
                        <div>
                          Show: {template.showTotalTreatmentTime ? 'Yes' : 'No'}
                        </div>
                        <div>
                          Required:{' '}
                          {template.totalTreatmentTimeRequired ? 'Yes' : 'No'}
                        </div>
                        <div>
                          Default:{' '}
                          {template.defaultTotalMinutes === null ||
                          template.defaultTotalMinutes === undefined
                            ? 'None'
                            : `${template.defaultTotalMinutes} min`}
                        </div>
                      </td>
                      <td>{template.copyForward ? 'Yes' : 'No'}</td>
                      <td>
                        <span
                          className={`clinical-template-status-badge ${
                            template.active ? 'active' : 'inactive'
                          }`}
                        >
                          {template.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div>Created by: {template.createdByStaffId ?? 'N/A'}</div>
                        <div>{formatDateTime(template.createdAt)}</div>
                        <div>Updated by: {template.updatedByStaffId ?? 'N/A'}</div>
                        <div>{formatDateTime(template.updatedAt)}</div>
                      </td>
                      <td>
                        <div className="clinical-template-row-actions">
                          <button
                            type="button"
                            className="clinical-template-secondary-button"
                            onClick={() => beginEdit(template)}
                            disabled={saving || busy}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className={
                              template.active
                                ? 'clinical-template-danger-button'
                                : 'clinical-template-activate-button'
                            }
                            onClick={() => toggleActive(template)}
                            disabled={saving || busy}
                          >
                            {busy
                              ? 'Updating...'
                              : template.active
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
